import { ReactNode, useEffect, useRef, useState } from "react";
import Search from "../Icons/Search";
import StarRating from "../StarRating";

type OptionType = {
  label: string;
  value: string | number;
  rating?: number;
  ratingColor? : string;
};

type CustomSelectProps = {
  options: OptionType[];
  placeholder?: string;
  onSelect: (option: OptionType) => void;
  textOnly?: boolean;
  showStarRating?: boolean;
  enableFilter?: boolean;
  icon?: ReactNode;
  defaultSelected?: OptionType;
  value?: OptionType | null;
  disabled?: boolean;
  onDropdownOpen?: () => void;
  scrollToTimeOnOpen?: string; // e.g., '07:00'
  maxOptionHeight?: number; // in pixels
};

const CustomSelect = ({
  options,
  placeholder = "Select an option",
  onSelect,
  textOnly = false,
  showStarRating = false,
  enableFilter = true,
  icon,
  defaultSelected,
  value,
  disabled = false,
  onDropdownOpen,
  scrollToTimeOnOpen,
  maxOptionHeight = 200,
}: CustomSelectProps) => {
  const [selected, setSelected] = useState<OptionType | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const typedCharRef = useRef<string>("");
  const matchIndexRef = useRef<number>(0);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && scrollToTimeOnOpen && listRef.current) {
      const target = listRef.current.querySelector(
        `li[data-value="${scrollToTimeOnOpen}"]`
      );
      if (target) {
        (target as HTMLLIElement).scrollIntoView({ block: "start" });
      }
    }
  }, [isOpen, scrollToTimeOnOpen]);

  // Type-to-navigate: when dropdown is open and filter is disabled,
  // pressing a digit scrolls to the first (or next) matching option.
  useEffect(() => {
    if (!isOpen || enableFilter) return;

    // Reset navigation state each time the dropdown opens
    typedCharRef.current = "";
    matchIndexRef.current = 0;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!/^\d$/.test(e.key)) return;

      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);

      if (e.key === typedCharRef.current) {
        // Same digit pressed again — cycle to the next match
        matchIndexRef.current += 1;
      } else {
        // New digit — start from the first match
        typedCharRef.current = e.key;
        matchIndexRef.current = 0;
      }

      if (listRef.current) {
        const items = Array.from(listRef.current.children) as HTMLElement[];
        const matches = items.filter((child) =>
          child.textContent?.trimStart().startsWith(e.key)
        );

        if (matches.length) {
          matchIndexRef.current = matchIndexRef.current % matches.length;
          matches[matchIndexRef.current].scrollIntoView({ block: "start" });
        }
      }

      // Reset after 800 ms of no further input
      typingTimerRef.current = setTimeout(() => {
        typedCharRef.current = "";
        matchIndexRef.current = 0;
      }, 800);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [isOpen, enableFilter]);

  // Filter options only if enableFilter is true
  const filteredOptions = enableFilter
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  const handleSelect = (option: OptionType) => {
    if (disabled) return;
    setSelected(option);
    setIsOpen(false);
    setSearch("");
    if (onSelect) {
      onSelect(option);
    }
  };

  useEffect(() => {
    if (defaultSelected) {
      setSelected(defaultSelected);
    }
  }, [defaultSelected]);

  useEffect(() => {
    if (value) {
      setSelected(value);
    } else if (value === null) {
      setSelected(null);
    }
  }, [value]);

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen && onDropdownOpen) {
        onDropdownOpen(); // will run once because of parent useRef guard
      }
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <div className="custom-select position-relative" ref={dropdownRef}>
      {/* Selected Item */}
      <div
        className={` 
          ${icon ? "py-7px ps-10px pe-15px" : ""}   
          ${
            textOnly
              ? "px-0 py-0"
              : "bg-white border border-off-white radius-8px py-10px px-15px"
          }
          d-flex gap-3 align-items-center justify-content-between 
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        onClick={handleToggle}
      >
        <div className="d-flex gap-8 align-items-center">
          {icon && <>{icon}</>}
          <div
            className={`${selected ? "text-black" : "text-grey-medium"} ${
              textOnly ? "fs-13 fw-700" : ""
            }`}
          >
            {selected ? selected.label : placeholder}
          </div>
          {showStarRating && selected && (
            <StarRating rating={selected.rating || 0} color={selected.ratingColor || "#51C2B9"} />
          )}
        </div>
        <svg
          className={`min-w-12px ${isOpen ? "rotate-180" : "rotate-0"}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.5 4.0575L9.4425 3L6 6.435L2.5575 3L1.5 4.0575L6 8.5575L10.5 4.0575Z"
            fill="#757575"
          />
        </svg>
      </div>
      {/* Dropdown List */}
      {isOpen && (
        <div
          className={`dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px overflow-hidden ${
            textOnly ? "min-w-130px start-100" : " w-100"
          }`}
        >
          {enableFilter && (
            <div className="p-6px border-bottom border-grey-light">
              <div className="form-group input-icon icon-left">
                <Search />
                <input
                  type="text"
                  className="form-control border-grey-light"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearch(val);

                    if (!enableFilter && listRef.current && val) {
                      const match = Array.from(listRef.current.children).find(
                        (child) =>
                          (child as HTMLElement).textContent
                            ?.toLowerCase()
                            .startsWith(val.toLowerCase())
                      );
                      if (match) {
                        (match as HTMLElement).scrollIntoView({
                          block: "start",
                        });
                      }
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  disabled={disabled}
                />
              </div>
            </div>
          )}
          <ul
            ref={listRef}
            className={`list-unstyled m-0 overflow-y-auto`}
            style={{ maxHeight: `${maxOptionHeight}px` }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt: OptionType) => (
                <li
                  className="cursor-pointer hover-bg-off-white px-15px py-10px fs-16 fw-400 text-black lh-20px d-flex gap-8 text-break"
                  key={opt.value}
                  data-value={opt.value}
                  onClick={() => handleSelect(opt)}
                >
                  <span className="text-truncate">{opt.label}</span>
                  {textOnly && selected && selected.value === opt.value && (
                    <svg
                      className="min-w-24px ms-auto"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16.3683 9.13327C16.5445 9.31036 16.5438 9.59679 16.3667 9.77303L10.4575 15.654C10.281 15.8296 9.99577 15.8296 9.81928 15.654L7.13327 12.9808C6.95618 12.8046 6.95549 12.5181 7.13173 12.3411C7.30798 12.164 7.59441 12.1633 7.77149 12.3395L10.1384 14.6951L15.7285 9.13173C15.9056 8.95549 16.192 8.95618 16.3683 9.13327Z"
                        fill="black"
                        stroke="black"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  {showStarRating && (
                    <StarRating rating={opt.rating || 0} color={opt.ratingColor || "#51C2B9"} />
                  )}
                </li>
              ))
            ) : (
              <li className="no-options px-15px py-10px fs-16 fw-400 text-grey-medium lh-20px d-block">
                No results found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
